import { getToken } from '@verantos-dev/core-auth';

// Read the API origin directly from Vite env instead of importing
// `getBaseApiUrl`/`getGatewayApiVersion` from `@verantos-dev/core-constants`.
// `core-utils` is tagged `scope:shared` and the Nx boundary lint forbids
// imports from non-shared libs. `createBaseQuery` in this same package
// inlines the same env read for the same reason.
const GATEWAY_API_PATH = 'v1/message-gateway';
const getBaseUrl = (): string => `${import.meta.env.VITE_BASE_API_URL}/${GATEWAY_API_PATH}`;

/**
 * Auth-aware fetch utility for TanStack Query hooks.
 *
 * Mirrors the auth pattern from `createBaseQuery` (RTK Query) but uses
 * plain `fetch` so it works with any data-fetching library. Throws on
 * non-2xx responses with the status + statusText in the message.
 */
export async function authFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const baseUrl = getBaseUrl();
  const token = await getToken();

  const response = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

/**
 * Auth-aware fetch for binary/file responses (e.g. CSV downloads). Same auth +
 * base-url + error handling as {@link authFetch}, but returns the raw `Blob`
 * instead of parsing JSON — and does NOT force a JSON `Content-Type` (it's used
 * for GETs that stream a file attachment back).
 */
export async function authFetchBlob(path: string, init?: RequestInit): Promise<Blob> {
  const baseUrl = getBaseUrl();
  const token = await getToken();

  const response = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }

  return response.blob();
}
